import { useMemo, useState } from 'react';
import { Command } from 'cmdk';
import { Popover } from 'radix-ui';
import { CircleSlash, Plus } from 'lucide-react';
import type { Project, Task } from '@timato/shared';

import { ProjectChip } from '@/components/ProjectChip';
import { ProjectDot } from '@/components/ProjectDot';
import { TaskOption, taskOptionClass as itemClass, useLockHolder, useTaskSuggestions } from '@/components/tasks';
import { useLabels, useProjects } from '@/data/hooks';
import { LabelChip } from '@/components/labels';
import { createTask } from '@/data/tasks';
import { cn } from '@/lib/cn';
import { projectColor } from '@/lib/projectColors';
import { useShortcuts } from '@/lib/useShortcuts';
import { goalFromTask, type Goal } from '@/timer/goal';

type Props = { goal: Goal; onChange: (g: Goal) => void; /** Render the list inline (inside a sheet) instead of in a popover. */ startOpen?: boolean };

/**
 * The goal field (spec §3.2): a combobox, not a text box. Type to create a task in the chip's project, or pick a saved
 * open task from any project. cmdk provides the listbox, keyboard navigation and aria roles; filtering is ours.
 */
export function GoalCombobox({ goal, onChange, startOpen = false }: Props) {
  const projects = useProjects().filter((p) => !p.archivedAt);
  const lockedBy = useLockHolder();
  const projectById = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);
  const [open, setOpen] = useState(startOpen);
  const [query, setQuery] = useState('');
  // An override that only lives as long as the open palette: it aims "Create …" at a project before a task exists.
  // Once a task is picked, that task's project is the truth, so the override is dropped.
  const [chip, setChip] = useState<Project | null>(null);
  const [labelFilter, setLabelFilter] = useState<string | null>(null);
  const labels = useLabels().filter((l) => !l.retiredAt);

  // What the goal actually points at. The closed field shows this, so an empty goal shows no project.
  const goalProject = goal.projectId ? projectById.get(goal.projectId) ?? null : null;
  // Where a *new* task would go: the goal's project, else the one picked by hand, else the first project.
  const chipProject = chip ?? goalProject ?? projects[0] ?? null;
  const hasGoal = !!(goal.taskId || goal.projectId || goal.goalText);

  const setOpenReset = (o: boolean) => { setOpen(o); if (o) { setQuery(''); setLabelFilter(null); setChip(null); } };
  useShortcuts(useMemo(() => [{ key: 'g', handler: () => setOpenReset(true) }], []));

  const raw = query.trim().toLowerCase();
  // Typing # switches the list to labels; picking one filters the tasks below (8e).
  const labelMode = raw.startsWith('#');
  const q = labelMode ? '' : raw;
  const labelQuery = labelMode ? raw.slice(1) : '';
  const labelMatches = labelMode ? labels.filter((l) => !labelQuery || l.name.toLowerCase().includes(labelQuery)) : [];
  const activeLabel = labelFilter ? labels.find((l) => l.id === labelFilter) : null;
  // The chip's project leads, then whatever you worked on most recently.
  const { tasks, found, matches, inProject: inChip, exact } = useTaskSuggestions(q, chipProject?.id ?? null, labelFilter);
  const chipTasks = matches.filter(inChip);
  const otherTasks = matches.filter((t) => !inChip(t));
  /** Split into two headed groups only when both would carry something; one heading otherwise, as in 4a. */
  const split = chipTasks.length > 0 && otherTasks.length > 0;

  const pick = (t: Task) => { if (lockedBy(t) && navigator.onLine) return; setChip(null); onChange(goalFromTask(t)); setOpen(false); };
  /** Track mode runs happily against nothing at all, and a session with no goal is a real row (8f). */
  const clear = () => { setChip(null); onChange({ taskId: null, projectId: null, goalText: '' }); setOpen(false); };
  const create = async () => {
    const name = query.trim();
    if (!name) return;
    if (chipProject) { const t = await createTask(chipProject.id, name); onChange(goalFromTask(t)); }
    else onChange({ taskId: null, projectId: null, goalText: name }); // no projects yet: a plain goal (track without target)
    setOpen(false);
  };


  const taskItem = (t: Task) => <TaskOption key={t.id} task={t} project={projectById.get(t.projectId)} onSelect={() => pick(t)} locked />;

  const commandBody = (
    <Command shouldFilter={false} loop label="Goal">
      <div className="flex items-center gap-2 px-3 h-11 border-b border-track">
        <Command.Input autoFocus value={query} onValueChange={setQuery} placeholder="Type a goal, search tasks, # for labels…" className="flex-1 bg-transparent border-0 outline-none text-row-title text-ink placeholder:text-ink-2" />
        {projects.length > 0 && <ProjectChip projects={projects} value={chipProject} onChange={(p) => setChip(p)} />}
      </div>
      <Command.List className="max-h-[320px] overflow-y-auto py-1">
        {activeLabel && <div className="flex items-center gap-2 px-2 py-1 text-inline-meta text-ink-2">Tasks with <LabelChip label={activeLabel} onRemove={() => setLabelFilter(null)} /></div>}
        {labelMode && (
          <Command.Group heading="Labels" className={groupClass}>
            {labelMatches.map((l) => <Command.Item key={l.id} value={`label:${l.id}`} onSelect={() => { setLabelFilter(l.id); setQuery(''); }} className={itemClass}><LabelChip label={l} /><span className="ml-auto text-inline-meta text-ink-2">{(tasks ?? []).filter((t) => t.labelIds.includes(l.id)).length} tasks</span></Command.Item>)}
            {!labelMatches.length && <Command.Empty className="px-2 py-2 text-body-sm text-ink-2">No matching labels</Command.Empty>}
          </Command.Group>
        )}
        {q && !exact && (
          <Command.Item value={`create:${q}`} onSelect={() => void create()}
            className="flex items-center gap-[10px] px-3 py-[11px] bg-canvas border-b border-track text-[13px] text-ink cursor-pointer select-none data-[selected=true]:bg-inset">
            <span className="w-[22px] h-[22px] rounded-chip bg-primary-btn-bg text-primary-btn-fg grid place-items-center font-extrabold text-[14px] shrink-0"><Plus size={13} strokeWidth={3} /></span>
            <span className="flex-1 truncate">Create <b>“{query.trim()}”</b>{chipProject ? <> in <span className="font-bold" style={{ color: projectColor(chipProject) }}>{chipProject.name}</span></> : ' as a goal'}</span>
            <span className="font-mono text-[11px] text-ink-2">↵</span>
          </Command.Item>
        )}
        {!labelMode && hasGoal && !q && (
          <Command.Item value="clear-goal" onSelect={clear} className={itemClass}>
            <CircleSlash size={14} className="text-ink-2 shrink-0" />
            <span className="truncate">Track without a goal</span>
          </Command.Item>
        )}
        {!labelMode && (split ? (
          <>
            <Command.Group heading={chipProject!.name} className={groupClass}>{chipTasks.map(taskItem)}</Command.Group>
            <Command.Group heading="Other projects" className={groupClass}>{otherTasks.map(taskItem)}</Command.Group>
          </>
        ) : (
          <Command.Group heading="Saved tasks" className={groupClass}>
            {matches.map(taskItem)}
            {/* Silent until the read lands: "no open tasks" would be a guess. */}
            {tasks && matches.length === 0 && <Command.Empty className="px-2 py-2 text-body-sm text-ink-2">{tasks.length ? 'No matching tasks' : 'No open tasks yet - type to create one'}</Command.Empty>}
          </Command.Group>
        ))}
        {/* The cap is silent otherwise, and a shortlist that looks like the whole list is worse than a short one. */}
        {!labelMode && found.length > matches.length && (
          <p className="px-3 py-2 text-inline-meta text-ink-2 m-0">Showing {matches.length} of {found.length} · type to narrow</p>
        )}
      </Command.List>
      <div className="hidden md:flex items-center justify-between px-3 h-9 border-t border-track text-[11px] font-semibold text-ink-2">
        <span>↑↓ navigate · ↵ select</span><span>esc</span>
      </div>
    </Command>
  );

  // Inside a sheet the list is the whole surface; on desktop it hangs from the field in a popover.
  if (startOpen) return commandBody;

  return (
    <Popover.Root open={open} onOpenChange={setOpenReset}>
      <Popover.Trigger asChild>
        <button type="button" role="combobox" aria-expanded={open} aria-label="Goal for this session"
          className="flex items-center gap-2 h-11 pl-3 pr-2 rounded-input border border-border bg-card text-left cursor-text hover:border-ink-2 outline-none focus-visible:border-focus-ring">
          <span className={cn('flex-1 min-w-0 truncate text-[13.5px] font-bold', goal.goalText ? 'text-ink' : 'text-ink-2')}>{goal.goalText || 'What are you working on?'}</span>
          {goalProject && (
            <span className="inline-flex items-center gap-[6px] shrink-0 rounded-chip bg-inset px-2 py-1 text-[12px] font-bold text-ink">
              <ProjectDot project={goalProject} />{goalProject.name}
            </span>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="start" sideOffset={6} className="z-50 w-[var(--radix-popover-trigger-width)] min-w-[320px] rounded-input border border-border bg-card shadow-popover overflow-hidden outline-none">
          {commandBody}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

const groupClass = '[&_[cmdk-group-heading]]:text-[10.5px] [&_[cmdk-group-heading]]:font-extrabold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[.08em] [&_[cmdk-group-heading]]:text-ink-2 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1';
